/**
 * Sentry error tracking setup for BizConnect360's browser bundle (Vite,
 * source maps uploaded at build via @sentry/vite-plugin — see vite.config.ts).
 *
 * WiBiz standard: every service reads its DSN from an env var and no-ops
 * when unset, so local dev is unaffected. See WiBiz OS `06 WiBiz/Monitoring/`
 * for the org-wide rollout doc. Errors-only for this canary — no Replay, no
 * session tracking, no tracing beyond the explicit 0 default.
 *
 * Mirrors server/_core/sentry.ts's PII-key scrubber (Studio N's
 * sentry_utils.py ported to TS, applied on both surfaces of this repo).
 */

import * as Sentry from "@sentry/react";

const PII_DENYLIST = new Set([
  "phone",
  "phone_number",
  "phoneNumber",
  "whatsapp",
  "email",
  "ssn",
  "ein",
]);

// Recurses into nested objects/arrays - mirrors server/_core/sentry.ts's scrubRequestData.
// Depth-capped against pathological/cyclic structures rather than relying on them not existing.
export function scrubKeyedRecord(data: unknown, depth = 0): unknown {
  if (depth > 8) return data;
  if (Array.isArray(data)) {
    return data.map((item) => scrubKeyedRecord(item, depth + 1));
  }
  if (!data || typeof data !== "object") return data;
  const record = data as Record<string, unknown>;
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    scrubbed[key] =
      PII_DENYLIST.has(key) || PII_DENYLIST.has(key.toLowerCase())
        ? "[Filtered]"
        : scrubKeyedRecord(value, depth + 1);
  }
  return scrubbed;
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.request?.data !== undefined) {
    event.request.data = scrubKeyedRecord(event.request.data);
  }
  if (event.extra) {
    event.extra = scrubKeyedRecord(event.extra) as Record<string, unknown>;
  }
  return event;
}

/** Call before the React root mounts — see client/src/main.tsx. */
export function initClientSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return; // local dev / no DSN configured — no-op

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "production",
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend,
  });
}
